import { useCallback, useState } from 'react';

import { useMutation } from '@apollo/react-hooks';
import { useQuery } from 'react-query';

import SOCIAL_AUTH from 'api/graphql/mutations/user/SOCIAL_AUTH';
import publicKeysApi from 'api/rest/publicKeys/publicKeys';
import messages from 'constants/dictionaries/messages';
import OAUTH_PROVIDERS from 'constants/dictionaries/oauthProviders';
import QUERY from 'constants/queryKeys';
import { useAuthContext } from 'context/AuthContext';
import { useUserLogContext } from 'context/UserLogContext/UserLogContext';
import useCommitError from 'services/common/useCommitError/useCommitError';
import useSafeMutation from 'services/common/useSafeMutation/useSafeMutation';
import type { GoogleTokenResponse } from 'types/googleIdentityServices';

import loadGoogleIdentityServices from './loadGoogleIdentityServices';

const GOOGLE_SCOPE = 'openid email profile';
const GOOGLE_ERROR_MESSAGE = 'Nie udało się zalogować przy pomocy konta Google. Spróbuj za pomocą email.';

type UseGoogleAuthResult = {
  login: () => Promise<void>;
  isLoading: boolean;
  isReady: boolean;
};

const useGoogleAuth = (): UseGoogleAuthResult => {
  const { logEvent } = useUserLogContext();
  const [loginSocialMutation] = useMutation(SOCIAL_AUTH);
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore - AuthContext is not typed
  const { onLogin } = useAuthContext();
  const commitError = useCommitError();
  const safeMutation = useSafeMutation();

  const [isLoading, setIsLoading] = useState(false);

  const { data: publicKeys } = useQuery(QUERY.GET_PUBLIC_KEYS, publicKeysApi.get(), {
    staleTime: 12 * 60 * 60 * 1000, // 12 hours
  });
  const clientId = publicKeys?.googleClientId;

  const exchangeAccessToken = useCallback(
    async (accessToken: string) => {
      const variables = { accessToken, provider: OAUTH_PROVIDERS.GOOGLE };

      try {
        // @ts-ignore - safeMutation is not typed
        const { data } = await safeMutation(() => loginSocialMutation({ variables }), GOOGLE_ERROR_MESSAGE);

        if (data?.socialAuth?.token) {
          logEvent('login-google');
          onLogin(data.socialAuth.token);
          return;
        }

        commitError({ message: 'Google login sync issue' }, messages.ERROR.LOGIN(), 'useGoogleAuth');
      } finally {
        setIsLoading(false);
      }
    },
    [safeMutation, loginSocialMutation, logEvent, onLogin, commitError],
  );

  const handleTokenResponse = useCallback(
    (response: GoogleTokenResponse) => {
      if (response.error || !response.access_token) {
        commitError({ message: 'Google login issue', props: response }, messages.ERROR.LOGIN(), 'useGoogleAuth');
        setIsLoading(false);
        return;
      }

      exchangeAccessToken(response.access_token);
    },
    [exchangeAccessToken, commitError],
  );

  const login = useCallback(async () => {
    if (!clientId) {
      commitError({ message: 'Missing Google client id' }, messages.ERROR.LOGIN(), 'useGoogleAuth');
      return;
    }

    setIsLoading(true);

    try {
      await loadGoogleIdentityServices();

      const tokenClient = window.google?.accounts.oauth2.initTokenClient({
        client_id: clientId,
        scope: GOOGLE_SCOPE,
        callback: handleTokenResponse,
        error_callback: error => {
          // The user closing the popup is an expected cancellation, not an error to report.
          if (error.type !== 'popup_closed') {
            commitError({ message: 'Google login popup issue', props: error }, messages.ERROR.LOGIN(), 'useGoogleAuth');
          }
          setIsLoading(false);
        },
      });

      if (!tokenClient) {
        throw new Error('Google Identity Services unavailable');
      }

      tokenClient.requestAccessToken();
    } catch (error) {
      commitError({ message: 'Google Identity Services load issue', props: error }, messages.ERROR.LOGIN(), 'useGoogleAuth');
      setIsLoading(false);
    }
  }, [clientId, handleTokenResponse, commitError]);

  return { login, isLoading, isReady: Boolean(clientId) };
};

export default useGoogleAuth;
